import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { ResponsiveContainer, Line, LineChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar } from "recharts";
import Button from 'screens/components/basic/Button';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { AgCharts } from 'ag-charts-react';
import 'ag-charts-enterprise';

const DataChart = () => {
    // Redux State
    const { hourlySalesSecondary } = useSelector(state => state.reports || {});

    // Local State
    const [showPlots, setShowPlots] = useState(false);
    const [plotNetQuan, setPlotNetQuan] = useState(false);
    const [plotNetSale, setPlotNetSale] = useState(true);
    const [plotCost, setPlotCost] = useState(true);
    const [plotProfit, setPlotProfit] = useState(true);
    const [plotGP, setPlotGP] = useState(false);
    
    const togglePlotVisability = (event, plot) => {
        switch (plot) {
            case "Net Quantity":
                setPlotNetQuan(event.target.checked);
                break;
            case "Gross Sale":
                setPlotNetSale(event.target.checked);
                break;
            case "Cost":
                setPlotCost(event.target.checked);
                break;
            case "Profit":
                setPlotProfit(event.target.checked);
                break;
            case "GP%":
                setPlotGP(event.target.checked);
                break;
            default:
                break;
        }
    };

    const plotlist = [
        {
            label: "Net Quantity",
            state: plotNetQuan,
            key: "qty"
        },
        {
            label: "Gross Sale",
            state: plotNetSale,
            key: "grossSales"
        },
        {
            label: "Cost",
            state: plotCost,
            key: "cost"
        },
        {
            label: "Profit",
            state: plotProfit,
            key: "profit"
        },
        {
            label: "GP%",
            state: plotGP,
            key: "gpm"
        }
    ];

    const renderer = (params) => {
        const plotLines = plotlist.filter(item => item.key !== "qty" && item.key !== "gpm").map((plot) => {
            const plotValue = params.datum[plot.key] || 0;
            return `
                <div class="custom-tooltip-plot-line">
                    <b>${plot.label}</b>: $${plotValue.toFixed(2)}
                </div>`;
        }).join('');
    
        // Return the complete tooltip HTML structure
        return `
            <div class="ag-chart-tooltip-custom">
                <b class="title">${params.datum.time % 24}:00 - ${(params.datum.time % 24 + 1)}:00</b><br/>
                ${plotLines}
            </div>
        `;
    };

    const netQuanchartOptions = {
        data: hourlySalesSecondary || [],
        series: [
            {
                type: 'line',
                xKey: 'time',       // X-axis key (Hours)
                yKey: 'qty',      // Y-axis key (Quantity)
                yName: `Net Quantity`,
                stroke: '#7A60E5',
                interpolation: { type: "smooth" },
                marker: {
                    enabled: true,            // Enable the markers (dots)
                    size: 5,                  // Set the size of the dot
                    shape: 'circle',          // Set the shape of the dot ('circle', 'square', etc.)
                    fill: '#f8f9fa',              // Dot color
                    stroke: '#7A60E5',          // Border color of the dot
                    strokeWidth: 1            // Border width of the dot
                },
            }
        ],
        axes: [
          {
            type: 'time',  // X-axis is a plot axis for the date
            position: 'bottom',
            label: {
                formatter: ({ value }) => `${value % 24}:00 - ${(value % 24 + 1)}:00`,
            },
            gridLine: {
                enabled: true,
            },
          },
          {
            type: 'number',    // Y-axis is a number axis for sales
            position: 'left',
            gridLine: {
                enabled: true,
            },
            title:{
                text: 'Quantity'
            }
          },
        ],
        background: {
            fill: '#fff', // Set background color here
        },
    };

    const chartOptions = {
        data: hourlySalesSecondary || [],
        series: [
            {
                type: 'line',
                xKey: 'time',       // X-axis key (Hours)
                yKey: 'grossSales',      // Y-axis key (Quantity)
                yName: `NetSales`,
                stroke: '#7A60E5',
                interpolation: { type: "smooth" },
                marker: {
                    enabled: true,            // Enable the markers (dots)
                    size: 5,                  // Set the size of the dot
                    shape: 'circle',          // Set the shape of the dot ('circle', 'square', etc.)
                    fill: '#f8f9fa',              // Dot color
                    stroke: '#7A60E5',          // Border color of the dot
                    strokeWidth: 1            // Border width of the dot
                },
                tooltip: { renderer },
                visible: plotNetSale
            },
            {
                type: 'line',
                xKey: 'time',       // X-axis key (Hours)
                yKey: 'cost',      // Y-axis key (Quantity)
                yName: `Cost`,
                stroke: '#a84d02',
                interpolation: { type: "smooth" },
                marker: {
                    enabled: true,            // Enable the markers (dots)
                    size: 5,                  // Set the size of the dot
                    shape: 'circle',          // Set the shape of the dot ('circle', 'square', etc.)
                    fill: '#f8f9fa',              // Dot color
                    stroke: '#a84d02',          // Border color of the dot
                    strokeWidth: 1            // Border width of the dot
                },
                tooltip: { renderer },
                visible: plotCost
            },
            {
                type: 'line',
                xKey: 'time',       // X-axis key (Hours)
                yKey: 'profit',      // Y-axis key (Quantity)
                yName: `Profit`,
                stroke: '#11f5b4',
                interpolation: { type: "smooth" },
                marker: {
                    enabled: true,            // Enable the markers (dots)
                    size: 5,                  // Set the size of the dot
                    shape: 'circle',          // Set the shape of the dot ('circle', 'square', etc.)
                    fill: '#f8f9fa',              // Dot color
                    stroke: '#11f5b4',          // Border color of the dot
                    strokeWidth: 1            // Border width of the dot
                },
                tooltip: { renderer },
                visible: plotProfit
            }
        ],
        axes: [
          {
            type: 'time',  // X-axis is a plot axis for the date
            position: 'bottom',
            label: {
                formatter: ({ value }) => `${value % 24}:00 - ${(value % 24 + 1)}:00`,
            },
            gridLine: {
                enabled: true,
            },
            min: 9,
            nice: false
          },
          {
            type: 'number',    // Y-axis is a number axis for sales
            position: 'left',
            gridLine: {
                enabled: true,
            },
            label: {
                formatter: ({ value }) => `$${value.toFixed(0)}`,
            },
            title:{
                text: 'Dollars'
            },
            min: -20,
            nice: false
          },
        ],
        background: {
            fill: '#fff', // Set background color here
        },
    };

    const gpChartOptions = {
        data: hourlySalesSecondary || [],
        series: [
            {
                type: 'line',
                xKey: 'time',       // X-axis key (Hours)
                yKey: 'gpm',      // Y-axis key (Quantity)
                yName: `Average GP %`,
                stroke: '#7A60E5',
                interpolation: { type: "smooth" },
                marker: {
                    enabled: true,            // Enable the markers (dots)
                    size: 5,                  // Set the size of the dot
                    shape: 'circle',          // Set the shape of the dot ('circle', 'square', etc.)
                    fill: '#f8f9fa',              // Dot color
                    stroke: '#7A60E5',          // Border color of the dot
                    strokeWidth: 1            // Border width of the dot
                },
            },
        ],
        axes: [
          {
            type: 'time',  // X-axis is a plot axis for the date
            position: 'bottom',
            label: {
                formatter: ({ value }) => `${value % 24}:00 - ${(value % 24 + 1)}:00`,
            },
            gridLine: {
                enabled: true,
            },
            min: 9,
            nice: false
          },
          {
            type: 'number',    // Y-axis is a number axis for sales
            position: 'left',
            gridLine: {
                enabled: true,
            },
            label: {
                formatter: ({ value }) => `${value.toFixed(0)}`,
            },
            title:{
                text: 'Perecentage'
            },
            min: -20,
            nice: false
          },
        ],
        background: {
            fill: '#fff', // Set background color here
        },
    };

    return (
        <div className="row mt-3">
            <div className="col-lg-2">
                <Button
                    type="IconInButton"
                    className="mt-2 mb-5 btnNewBrandPOS"
                    label={"Show Plots"}
                    icon={<VisibilityIcon />}
                    onTouchTap={() => setShowPlots(!showPlots)} />
                    <FormGroup>
                    {showPlots && plotlist.map((item) => (
                        <div>
                            <FormControlLabel control={<Checkbox
                                checked={item.state}
                                onClick={(e) => togglePlotVisability(e, item.label)}
                                name={item.label}
                            />} label={item.label} />
                        </div>)
                    )}
                    </FormGroup>
            </div>
            {showPlots ? <div className="col-lg-10">
                {plotNetQuan &&
                    <React.Fragment>
                        <h3 className="mt-0 mb-2 text-center text-justify">{"Average Net Quantity"}</h3>
                        <div style={{ height: 400 }}>
                            <AgCharts options={netQuanchartOptions} style={{ height: 400 }}/>
                        </div>
                    </React.Fragment>
                }
                {(plotNetSale || plotCost || plotProfit) &&
                    <React.Fragment>
                        <h3 className="mt-2 text-center text-justify">{"Average Gross Sales, Cost, Profit"}</h3>
                        <div style={{ height: 400 }}>
                            <AgCharts options={chartOptions} style={{ height: 400 }}/>
                        </div>
                    </React.Fragment>
                }
                {plotGP &&
                    <React.Fragment>
                        <h3 className="mt-2 text-center text-justify">{"Average GP %"}</h3>
                        <div style={{ height: 400 }}>
                            <AgCharts options={gpChartOptions} style={{ height: 400 }}/>
                        </div>
                    </React.Fragment>
                }
            </div> : null}
        </div>
    )
}

export default DataChart;